import axios from 'axios';

// ---------------------------------------------------------------------------
// AUTO PAYMENTS = A SEPARATE, EXPLICIT SIGN-UP (2026-07-18)
// ---------------------------------------------------------------------------
// Incident that created this file: a client started a card checkout for AED
// 850.50 of price adjustments and closed the payment page. Thirteen seconds
// later the saved-card AUTO-CHARGE rail fired and collected all nine
// adjustments individually. He had a saved card but had never signed up for
// automatic payments — from his side, money left silently right after he had
// cancelled a payment.
//
// The cause: SAVING A CARD and CONSENTING TO BE AUTO-CHARGED were the same
// act. They are now two separate things:
//
//   * a saved card  = a stored token, for faster checkout. Nothing more.
//   * auto payments = an explicit, revocable sign-up, toggled in Account →
//                     Payment methods, that authorises charging at booking
//                     completion.
//
// The client can have the first without the second, and turning the second OFF
// is a guarantee: no client surface may pre-select, default to, or submit the
// auto-charge rail while it is off.
//
// FAIL-CLOSED: `readAutoPayEnabled` only returns true for an explicit `true`
// from the server. A backend that has not shipped the field yet (undefined),
// a 404, or a failed request all read as OFF — so the worst case is that a
// client is asked to pay, never that they are silently charged.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// FEATURE FLAG — auto-payment UI is HIDDEN for now (2026-07-20)
// ---------------------------------------------------------------------------
// We cannot run unattended charges yet: N-Genius MoTo/MIT is not enabled on our
// outlet, so the auto-charge rail can never actually fire. Rather than offer a
// promise we can't keep, EVERY client-facing auto-payment surface is hidden —
// the checkout auto-charge option, the checkout sign-up invitation, and the
// Account → Payment methods sign-up switch. Saving a card STILL works, but only
// as plain faster-checkout storage in Account → Payment methods.
//
// This is a DISPLAY gate only. The deciders below (defaultPayMode /
// reevaluatePayMode) and the whole server rail are left intact and fail-closed,
// so flipping this back to `true` — the day MoTo/MIT goes live — re-exposes the
// exact same, already-tested flow with no other change. Nothing about how money
// moves changes here; this only decides what is OFFERED.
//
// TRUTHFULNESS CARVE-OUT: this flag hides forward-looking OFFERS, not the
// historical record. Receipts, funding-leg labels, and the card-removal guard
// that reflect a REAL past/pending auto-charge stay as they are — hiding a
// true statement about money that actually moved would be the opposite of the
// honesty this whole system is built on.
export const AUTO_CHARGE_UI_ENABLED = false;

// Owner's own framing, kept in one place so Account and checkout can't drift.
export const AUTO_PAY_TITLE = 'Sign up for simple auto payments';

export const AUTO_PAY_BLURB =
    'Save your card and pay automatically at booking completion. If a booking '
    + "price changes, we'll let you know before your card is charged.";

// The quiet invitation shown at checkout when the client has NOT signed up.
// Deliberately not a payment option — it is an offer, never a pre-selection.
export const AUTO_PAY_INVITE = 'Sign up for simple auto payment at booking completion';

// What "off" means, stated as the promise it is.
export const AUTO_PAY_OFF_GUARANTEE =
    'Automatic payments are off. Your saved cards stay on file for faster '
    + 'checkout only — nothing will ever be charged without you starting the '
    + 'payment yourself.';

export const AUTO_PAY_ON_SUMMARY =
    'Automatic payments are on. Completed cleanings are charged to your saved '
    + "card, and we'll tell you before charging any price change.";

export const AUTO_PAY_ENDPOINT = '/api/cards/auto-pay';

/**
 * Read the sign-up state off a GET /api/cards response body.
 * Strict `=== true` on purpose — see FAIL-CLOSED above.
 */
export const readAutoPayEnabled = (data) => {
    // THE SHAPE THE SERVER ACTUALLY SENDS (owner-reported 2026-07-19). GET /api/cards returns
    // the consent NESTED — `auto_pay: { enabled, consented_at, … }` from
    // autoPayConsent.autoPayPublicState — and PUT /api/cards/auto-pay echoes the
    // same block. This reader only ever looked for a FLAT `auto_pay_enabled`,
    // which the server has never sent, so it read `undefined` and fail-closed to
    // OFF forever. Mobile read the nested block correctly, hence the two clients
    // disagreeing about a single stored fact.
    //
    // Read the nested shape first, keep the flat one as a fallback in case an
    // older/other endpoint still answers that way. Strict `=== true` on BOTH —
    // see FAIL-CLOSED above; a mis-shaped payload must never read as consent.
    if (data?.auto_pay && typeof data.auto_pay === 'object') {
        return data.auto_pay.enabled === true;
    }
    return data?.auto_pay_enabled === true;
};

/* ---------------------------------------------------------------------------
 * CHECKOUT DEFAULT HIERARCHY (item 7, owner 2026-07-19 — refined the same day)
 * ---------------------------------------------------------------------------
 * THE shared pre-selection decider for the checkout surfaces, mirroring the
 * mobile reference implementation (hooks/clientHooks/autoPayConsent.ts →
 * defaultPayMode / reevaluatePayMode) so the two platforms cannot drift:
 *
 *   1. available balance > 0 (ANY amount — "even if its only 5 aed") →
 *      'paynow' with the wallet toggle on. A partial balance splits with the
 *      card inside the SAME Pay now action (wallet_card) — it NEVER falls
 *      through to auto-charge.
 *   2. available balance ZERO (or negative — legacy data; nothing to spend)
 *      AND enrolled AND chargeable → 'autocharge'. Enrolment means the
 *      explicit sign-up (fail-closed via readAutoPayEnabled). CHARGEABLE means
 *      the rail could actually RUN if selected: MoTo/MIT is enabled on the
 *      outlet AND there is something to charge — a saved card (the unattended
 *      rail) or the first-card AED 1.00 setup rail offered on the surface. A
 *      saved card alone is NOT required (the no-card case is exactly what the
 *      setup rail exists for), but consent with NO runnable rail must never
 *      pre-select an auto-charge that cannot happen — that client gets
 *      branch 3: Pay now by card, plus the quiet invitation.
 *   3. otherwise → 'paynow' (card) — the surface shows the quiet sign-up
 *      invitation.
 *
 * `available` is the plain AVAILABLE balance — the wallet headline itself
 * (item 12: due price adjustments never deduct from, net into, gate, or
 * ceiling it; a spendable-minus-owed figure must never be fed here). It is
 * UNKNOWN until the server quote arrives, so callers RE-EVALUATE when the
 * quote loads (not just when cards load); while unknown this FAILS CLOSED to
 * 'paynow' — there is no state of ignorance in which auto-charge is
 * pre-selected.
 *
 * This is a default PRE-SELECTION only. The client can always switch, and the
 * submission gates (the enrolment re-check at submit + the server) re-verify
 * everything.
 * ------------------------------------------------------------------------- */

// Half a fils — the same "is there effectively any money" tolerance as
// payNow.js / walletFigures.
const PAY_EPS = 0.005;

export function defaultPayMode({ enrolled, chargeable, available, cost }) {
    const avail = Number(available);
    // Balance unknown (quote not arrived / malformed) → fail closed to Pay now.
    if (!Number.isFinite(avail)) return 'paynow';
    // Rule 1 — ANY positive balance means the wallet goes first, even 5 AED.
    if (avail > PAY_EPS) return 'paynow';
    // Nothing to pay → nothing to auto-charge; keep the ordinary default.
    const amount = Number(cost);
    if (Number.isFinite(amount) && amount <= PAY_EPS) return 'paynow';
    // Rule 2 — zero balance, the explicit sign-up, AND a rail that can
    // actually run (chargeable = MIT enabled + card on file or the first-card
    // setup rail — the caller derives it; see the header). Strict `=== true`
    // on BOTH: the fail-closed discipline of readAutoPayEnabled carries
    // through here, so neither a mis-shaped flag nor an unknown MIT state can
    // pre-select the unattended rail — mirrors mobile's decider
    // (autoPayConsent.defaultPayMode), which gates rule 2 on mitEnabled the
    // same way.
    if (enrolled === true && chargeable === true) return 'autocharge';
    // Rule 3 — card + the sign-up invitation (also the landing spot for
    // consent-ON-but-not-chargeable: an auto-charge that cannot run is never
    // pre-selected).
    return 'paynow';
}

/**
 * The re-evaluation the checkout surfaces run whenever their inputs settle
 * (cards + enrolment resolve, the QUOTE arrives with the real cost and
 * available balance).
 *
 *   · A manual choice (`touched`) is NEVER overridden — not by the late
 *     quote, not by anything here.
 *   · An untouched default re-applies the hierarchy on every change — which
 *     is also the fail-closed path: defaultPayMode can never produce
 *     'autocharge' without `enrolled === true` AND `chargeable === true`, so
 *     a sign-up revoked elsewhere (or MIT flipping off) disarms an untouched
 *     auto-charge pre-selection the moment the state refreshes.
 */
export function reevaluatePayMode({ current, touched, enrolled, chargeable, available, cost }) {
    if (touched) return current;
    return defaultPayMode({ enrolled, chargeable, available, cost });
}

/**
 * Persist the sign-up. PUT /api/cards/auto-pay { enabled } → { auto_pay_enabled }.
 * Returns the server's value when it echoes one, otherwise the requested value.
 * Throws on failure so callers can keep the switch where it was and say so —
 * a toggle that silently fails is exactly the class of bug this feature exists
 * to remove.
 */
export async function saveAutoPayEnabled(enabled) {
    const res = await axios.put(AUTO_PAY_ENDPOINT, { enabled: !!enabled });
    // Same nested shape as GET (`{ message, auto_pay: { enabled, … } }`). Reading
    // only the flat key meant the echo never matched and this silently fell back
    // to the REQUESTED value — so a server that stored something different would
    // never have been noticed. Read the server's answer properly.
    const state = res?.data?.auto_pay;
    if (state && typeof state === 'object' && typeof state.enabled === 'boolean') {
        return state.enabled;
    }
    const echoed = res?.data?.auto_pay_enabled;
    return typeof echoed === 'boolean' ? echoed : !!enabled;
}
